import { useState } from 'react';
import { useBillingOverview } from '@/hooks/useBillingOverview';
import { useInvoices, useInvoicesExport } from '@/hooks/useInvoices';
import Header from '@/components/layout/header';
import BillingKpiStrip from '@/components/billing/BillingKpiStrip';
import BillingFilters from '@/components/billing/BillingFilters';
import RevenueTrendChart from '@/components/billing/RevenueTrendChart';
import InvoicesStatusChart from '@/components/billing/InvoicesStatusChart';
import TopClientsSpendChart from '@/components/billing/TopClientsSpendChart';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

export default function BillingOverviewPage() {
  const [dateRange, setDateRange] = useState<{ from?: Date; to?: Date }>({
    from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
    to: new Date()
  });
  const [clientTier, setClientTier] = useState('all');
  const [isExporting, setIsExporting] = useState(false);
  const { toast } = useToast();

  // Convert date range to string format for API
  const dateRangeParams = {
    dateFrom: dateRange?.from?.toISOString().split('T')[0],
    dateTo: dateRange?.to?.toISOString().split('T')[0]
  };

  // Fetch billing overview data
  const { 
    data: billingData, 
    isLoading: billingLoading, 
    error: billingError 
  } = useBillingOverview(dateRangeParams);

  // Fetch invoices data
  const { 
    data: invoices, 
    isLoading: invoicesLoading, 
    error: invoicesError 
  } = useInvoices({
    status: 'all',
    dateFrom: dateRangeParams.dateFrom,
    dateTo: dateRangeParams.dateTo,
    clientTier: clientTier as any
  });

  const { exportInvoices } = useInvoicesExport({
    status: 'all',
    dateFrom: dateRangeParams.dateFrom,
    dateTo: dateRangeParams.dateTo,
    clientTier: clientTier as any
  });

  const handleExport = async () => {
    try {
      setIsExporting(true);
      await exportInvoices();
      toast({
        title: "Export successful",
        description: "Your invoices CSV file has been downloaded.",
      });
    } catch (error) {
      toast({
        title: "Export failed",
        description: "Failed to export invoices. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  if (billingError || invoicesError) {
    return (
      <>
        <Header 
          title="Global Billing Dashboard" 
          description="Monitor revenue, invoices, and financial metrics across all clients"
        />
        <div className="flex-1 p-6">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Failed to load billing data. Please try again later.
            </AlertDescription>
          </Alert>
        </div>
      </>
    );
  }

  return (
    <>
      <Header 
        title="Global Billing Dashboard" 
        description="Monitor revenue, invoices, and financial metrics across all clients"
      />
      
      <div className="flex-1 overflow-auto p-6">
        <div className="space-y-6">
          {/* Filters */}
          <BillingFilters
            dateRange={dateRange}
            onDateRangeChange={(range) => setDateRange(range || {})}
            clientTier={clientTier}
            onClientTierChange={setClientTier}
            onExport={handleExport}
            isExporting={isExporting}
          />
          
          {/* KPI Strip */}
          <BillingKpiStrip 
            data={billingData ? {
              totalRevenue: billingData.totalRevenue,
              outstandingInvoices: billingData.outstandingInvoices,
              mrr: billingData.mrr,
              churnRate: billingData.churnRate
            } : undefined}
            loading={billingLoading}
          />
          
          {/* Charts Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <RevenueTrendChart 
              data={billingData?.revenueTrend} 
              loading={billingLoading}
            />
            <InvoicesStatusChart 
              data={billingData?.invoicesByStatus} 
              loading={billingLoading}
            />
          </div>
          
          {/* Top Clients Chart - Full Width */}
          <TopClientsSpendChart 
            data={billingData?.topClientsBySpend} 
            loading={billingLoading}
            className="lg:col-span-2"
          />
        </div>
      </div>
    </>
  );
}